import {
	createContext,
	type ReactElement,
	type ReactNode,
	useCallback,
	useContext,
	useId,
	useMemo,
	useState,
} from 'react';

import { css } from '@compiled/react';
import { UniformSlot } from '@uniformdev/canvas-react';
import { useIntl } from 'react-intl';

import { token } from '@atlaskit/tokens';

import { ContentBlock } from '@/libraries/design/components/content-block/src/content-block';
import { wacToken } from '@/libraries/design/foundations/index.ts';

interface AccordionsSectionContextValue {
	openItems: Record<string, boolean>;
	headingElement: 'h2' | 'h3';
	register: (id: string) => void;
	unregister: (id: string) => void;
	setOpen: (id: string, isOpen: boolean) => void;
}

const AccordionsSectionContext = createContext<AccordionsSectionContextValue | null>(null);

export type UseAccordionsSectionResult = AccordionsSectionContextValue | null;

export const useAccordionsSection = (): UseAccordionsSectionResult =>
	useContext(AccordionsSectionContext);

export interface UniformAccordionsSectionProps {
	heading: string;
	headingElement?: 'h2' | 'h3';
	subtitle?: ReactElement;
	anchorLinkId?: string;
	children?: ReactNode;
}

const UniformAccordionsSection = (props: UniformAccordionsSectionProps) => {
	const { heading, headingElement = 'h2', subtitle, anchorLinkId, children } = props;
	const intl = useIntl();
	const generatedHeadingId = useId();
	const headingId = anchorLinkId ?? generatedHeadingId;
	const toggleId = useId();
	const [openItems, setOpenItems] = useState<Record<string, boolean>>({});
	const itemIds = Object.keys(openItems);
	const allOpen = itemIds.length > 0 && itemIds.every((id) => openItems[id]);

	const register = useCallback((id: string) => {
		setOpenItems((items) => (id in items ? items : { ...items, [id]: false }));
	}, []);
	const unregister = useCallback((id: string) => {
		setOpenItems((items) => {
			if (!(id in items)) {
				return items;
			}
			const nextItems = { ...items };
			delete nextItems[id];
			return nextItems;
		});
	}, []);
	const setOpen = useCallback((id: string, isOpen: boolean) => {
		setOpenItems((items) => ({ ...items, [id]: isOpen }));
	}, []);
	const toggleAll = () => {
		setOpenItems((items) => Object.fromEntries(Object.keys(items).map((id) => [id, !allOpen])));
	};
	const contextValue = useMemo(
		() => ({ openItems, headingElement, register, unregister, setOpen }),
		[openItems, headingElement, register, unregister, setOpen],
	);

	return (
		<AccordionsSectionContext.Provider value={contextValue}>
			<section aria-labelledby={headingId}>
				<div css={headerCss}>
					<ContentBlock
						title={heading}
						titleAs={headingElement}
						text={subtitle}
						anchorLinkId={headingId}
					/>
					<button
						id={toggleId}
						type="button"
						css={toggleCss}
						aria-labelledby={`${toggleId} ${headingId}`}
						onClick={toggleAll}
						disabled={itemIds.length === 0}
					>
						{allOpen
							? intl.formatMessage({
									id: 'AccordionsSection.hideAll',
									defaultMessage: 'Hide all',
								})
							: intl.formatMessage({
									id: 'AccordionsSection.showAll',
									defaultMessage: 'Show all',
								})}
					</button>
				</div>
				{children ?? <UniformSlot name="accordions" />}
			</section>
		</AccordionsSectionContext.Provider>
	);
};

export default UniformAccordionsSection;

const headerCss = css({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-between',
	gap: token('space.200'),
	paddingBlockEnd: token('space.200'),
});

const toggleCss = css({
	flexShrink: 0,
	border: 0,
	padding: token('space.100'),
	backgroundColor: 'transparent',
	color: wacToken('color.extended.blue800'),
	cursor: 'pointer',
	'&:focus-visible': {
		outline: `2px solid ${wacToken('color.extended.blue800')}`,
		outlineOffset: token('space.050'),
	},
});
